"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import Button from "@/components/primitives/button";
import Icon from "@/components/primitives/icon";

const SHARE_WINDOW_FEATURES = "width=600,height=500,noopener,noreferrer";

export function openShareWindow(url: string) {
  if (typeof window === "undefined") return;
  window.open(url, "_blank", SHARE_WINDOW_FEATURES);
}

export function shareOnX(pageUrl: string, text?: string) {
  const params = new URLSearchParams({
    url: pageUrl,
    ...(text ? { text } : {}),
  });

  openShareWindow(`https://x.com/intent/tweet?${params.toString()}`);
}

export function shareOnFacebook(pageUrl: string) {
  const params = new URLSearchParams({
    u: pageUrl,
  });

  openShareWindow(
    `https://www.facebook.com/sharer/sharer.php?${params.toString()}`
  );
}

export async function copyShareLink(pageUrl: string) {
  if (typeof navigator === "undefined" || !navigator.clipboard) {
    throw new Error("Clipboard API is not available");
  }

  await navigator.clipboard.writeText(pageUrl);
}

export async function tryNativeShare(pageUrl: string, title?: string) {
  if (
    typeof navigator === "undefined" ||
    typeof navigator.share !== "function"
  ) {
    return false;
  }

  await navigator.share({
    url: pageUrl,
    ...(title ? { title } : {}),
  });

  return true;
}

type SharePopoverProps = {
  url: string;
  title?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  trigger?: React.ReactNode;
  className?: string;
};

export default function SharePopover({
  url,
  title,
  side = "bottom",
  align = "end",
  trigger,
  className,
}: SharePopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  async function onCopy() {
    try {
      await copyShareLink(url);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 5000); // reset after 5s
    } catch (err) {
      console.error("Copy failed", err);
    }
  }

  async function onNativeShare() {
    try {
      const usedNativeShare = await tryNativeShare(url, title);
      if (usedNativeShare) {
        setOpen(false);
      }
    } catch (error) {}
  }

  function onShareX() {
    shareOnX(url, title);
  }

  function onShareFacebook() {
    shareOnFacebook(url);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="circle"
            color="tertiary"
            size="sm"
            aria-label="Share"
          >
            <Icon name="share" size="lg" color="primary" />
          </Button>
        )}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={8}
          className={[
            "z-50 rounded-full bg-white border-secondary-700 shadow-card p-3",
            "border",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCopy}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 hover:bg-secondary-200 transition"
              aria-label={copied ? "Copied" : "Copy link"}
              title={copied ? "Copied" : "Copy link"}
            >
              <Icon name={copied ? "checkmark" : "copy-link"} size="md" color="primary" />
            </button>

            <button
              type="button"
              onClick={onShareX}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 hover:bg-secondary-200 transition"
              aria-label="Share on X"
              title="Share on X"
            >
              <Icon name="x" size="lg" color="primary" />
            </button>
            <button
              type="button"
              onClick={onShareFacebook}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 hover:bg-secondary-200 transition"
              aria-label="Share on Facebook"
              title="Share on Facebook"
            >
              <Icon name="facebook" size="lg" color="primary" />
            </button>

          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
